import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { privacyPolicySections, privacyPolicyMeta } from '../data/privacyPolicyContent';
import articleBg from '@/assets/article-bg.jpg';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div 
      className="min-h-screen bg-black text-gray-300 bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url(${articleBg})` }}
    >
      <div className="min-h-screen bg-black/80">
        <div className="px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto py-12 lg:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <div className="text-gray-400 space-y-1">
            <p>Effective Date: {privacyPolicyMeta.effectiveDate}</p>
            <p>Website: {privacyPolicyMeta.website}</p>
            <p>Company: {privacyPolicyMeta.company}</p>
            <p>Address: {privacyPolicyMeta.address}</p>
            <p>Contact: {privacyPolicyMeta.contact}</p>
          </div>
        </motion.header>

        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          <motion.nav
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block lg:col-span-1"
          >
            <div className="sticky top-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Table of Contents
              </h2>
              <ul className="space-y-2">
                {privacyPolicySections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className="text-sm text-gray-400 hover:text-orange-500 transition-colors duration-200 text-left"
                    >
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </motion.nav>

          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <p className="text-gray-400 mb-8 leading-relaxed">
              Animind is a business-to-business AI film and animation creation platform operated by ATELIER TENET INC. This Privacy Policy explains how we collect, use, disclose, store, and protect personal information when you visit our website, apply for access, communicate with us, or use our services.
            </p>
            <p className="text-gray-400 mb-12 leading-relaxed">
              Animind is intended for business users only. We do not knowingly provide our services to children or collect personal information from children.
            </p>

            {privacyPolicySections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="mb-10 scroll-mt-8"
              >
                <h2 className="text-xl font-semibold text-white mb-4">
                  {section.title}
                </h2>
                <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </section>
            ))}
          </motion.main>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
